'use client';

import { useState, useMemo, useCallback } from 'react';
import { formatDateShortYear } from '@/lib/date-format';

interface ScholarshipData {
  id: string;
  nameId: string;
  nameEn: string;
  provider: string;
  scholarshipType: string;
  descriptionId: string;
  descriptionEn: string;
  eligibilityId: string[];
  eligibilityEn: string[];
  benefitsId: string[];
  benefitsEn: string[];
  amount?: string;
  deadline?: string;
  url?: string;
  universities: string;
  linkSlug?: string;
}

interface ScholarshipScreenerProps {
  scholarships: ScholarshipData[];
  universityIds: string[];
  lang: string;
}

type Step = 'university' | 'income' | 'gpa' | 'results';

interface IncomeOption {
  id: string;
  labelId: string;
  labelEn: string;
}

interface GpaOption {
  id: string;
  labelId: string;
  labelEn: string;
}

const INCOME_OPTIONS: IncomeOption[] = [
  { id: 'below-4', labelId: '< Rp 4 juta/bulan', labelEn: '< Rp 4M/month' },
  { id: '4-8', labelId: 'Rp 4–8 juta/bulan', labelEn: 'Rp 4–8M/month' },
  { id: '8-15', labelId: 'Rp 8–15 juta/bulan', labelEn: 'Rp 8–15M/month' },
  { id: 'above-15', labelId: '> Rp 15 juta/bulan', labelEn: '> Rp 15M/month' },
];

const GPA_OPTIONS: GpaOption[] = [
  { id: 'above-3.5', labelId: 'IPK > 3,5', labelEn: 'GPA > 3.5' },
  { id: '3.0-3.5', labelId: 'IPK 3,0–3,5', labelEn: 'GPA 3.0–3.5' },
  { id: 'below-3.0', labelId: 'IPK < 3,0', labelEn: 'GPA < 3.0' },
];

const STEP_ORDER: Step[] = ['university', 'income', 'gpa', 'results'];

/**
 * Determine eligibility badge based on income and GPA selections.
 * Income below-4 and 4-8 are "likely eligible" for need-based;
 * GPA above-3.5 is "likely eligible" for merit-based.
 * Everything else gets "check eligibility".
 */
function getEligibilityBadge(
  scholarshipType: string,
  incomeId: string,
  gpaId: string,
  isId: boolean,
): { label: string; likely: boolean } {
  const isNeedBased = scholarshipType.toLowerCase().includes('need') ||
    scholarshipType.toLowerCase().includes('bantuan') ||
    scholarshipType.toLowerCase().includes('bidikmisi') ||
    scholarshipType.toLowerCase().includes('kip');
  const isMeritBased = scholarshipType.toLowerCase().includes('merit') ||
    scholarshipType.toLowerCase().includes('prestasi') ||
    scholarshipType.toLowerCase().includes('akademik');

  const lowIncome = incomeId === 'below-4' || incomeId === '4-8';
  const highGpa = gpaId === 'above-3.5';

  if (isNeedBased && lowIncome) {
    return { label: isId ? 'Kemungkinan memenuhi syarat' : 'Likely eligible', likely: true };
  }
  if (isMeritBased && highGpa) {
    return { label: isId ? 'Kemungkinan memenuhi syarat' : 'Likely eligible', likely: true };
  }
  if (!isNeedBased && !isMeritBased && (lowIncome || highGpa)) {
    return { label: isId ? 'Kemungkinan memenuhi syarat' : 'Likely eligible', likely: true };
  }

  return { label: isId ? 'Cek kelayakan' : 'Check eligibility', likely: false };
}

export function ScholarshipScreener({ scholarships, universityIds, lang }: ScholarshipScreenerProps) {
  const isId = lang === 'id';

  const [currentStep, setCurrentStep] = useState<Step>('university');
  const [selectedUnis, setSelectedUnis] = useState<Set<string>>(new Set());
  const [anyUniversity, setAnyUniversity] = useState(false);
  const [selectedIncome, setSelectedIncome] = useState<string>('');
  const [selectedGpa, setSelectedGpa] = useState<string>('');
  const [expandedCard, setExpandedCard] = useState<string | null>(null);

  const currentStepIndex = STEP_ORDER.indexOf(currentStep);

  const canProceedFromUniversity = anyUniversity || selectedUnis.size > 0;
  const canProceedFromIncome = selectedIncome !== '';
  const canProceedFromGpa = selectedGpa !== '';

  const goNext = useCallback(() => {
    const idx = STEP_ORDER.indexOf(currentStep);
    if (idx < STEP_ORDER.length - 1) {
      setCurrentStep(STEP_ORDER[idx + 1]);
    }
  }, [currentStep]);

  const goBack = useCallback(() => {
    const idx = STEP_ORDER.indexOf(currentStep);
    if (idx > 0) {
      setCurrentStep(STEP_ORDER[idx - 1]);
    }
  }, [currentStep]);

  const goToStep = useCallback((step: Step) => {
    const targetIdx = STEP_ORDER.indexOf(step);
    // Only allow navigating to completed steps or current step
    if (targetIdx <= currentStepIndex) {
      setCurrentStep(step);
    }
  }, [currentStepIndex]);

  const toggleUni = useCallback((uniId: string) => {
    setSelectedUnis(prev => {
      const next = new Set(prev);
      if (next.has(uniId)) next.delete(uniId);
      else next.add(uniId);
      return next;
    });
    setAnyUniversity(false);
  }, []);

  const selectAny = useCallback(() => {
    setAnyUniversity(true);
    setSelectedUnis(new Set());
  }, []);

  const resetAll = useCallback(() => {
    setCurrentStep('university');
    setSelectedUnis(new Set());
    setAnyUniversity(false);
    setSelectedIncome('');
    setSelectedGpa('');
    setExpandedCard(null);
  }, []);

  // Filter scholarships based on university selection
  const filteredScholarships = useMemo(() => {
    return scholarships.filter(s => {
      if (anyUniversity) return true;
      if (s.universities === 'all') return true;
      const uniList = s.universities.split(',').map(u => u.trim());
      return uniList.some(u => selectedUnis.has(u));
    });
  }, [scholarships, selectedUnis, anyUniversity]);

  const stepLabels: Record<Step, string> = {
    university: isId ? 'Universitas' : 'University',
    income: isId ? 'Penghasilan' : 'Income',
    gpa: isId ? 'IPK' : 'GPA',
    results: isId ? 'Hasil' : 'Results',
  };

  return (
    <div className="rounded-xl border border-border-subtle bg-surface-card p-4 md:p-6">
      <h3 className="font-heading text-lg font-bold text-text-primary">
        {isId ? 'Penyaring Beasiswa' : 'Scholarship Screener'}
      </h3>
      <p className="mt-1 text-sm text-text-secondary">
        {isId
          ? 'Jawab 3 pertanyaan singkat untuk menemukan beasiswa yang sesuai.'
          : 'Answer 3 quick questions to find scholarships that fit you.'}
      </p>

      {/* Step indicator */}
      <nav aria-label={isId ? 'Langkah penyaringan' : 'Screening steps'} className="mt-4">
        <ol className="flex items-center gap-2">
          {STEP_ORDER.map((step, idx) => {
            const isActive = step === currentStep;
            const isCompleted = idx < currentStepIndex;
            const isClickable = idx <= currentStepIndex;

            return (
              <li key={step} className="flex items-center gap-2">
                {idx > 0 && (
                  <div
                    className={`h-px w-4 sm:w-8 ${
                      isCompleted ? 'bg-accent-primary' : 'bg-border-subtle'
                    }`}
                    aria-hidden="true"
                  />
                )}
                <button
                  type="button"
                  onClick={() => goToStep(step)}
                  disabled={!isClickable}
                  aria-current={isActive ? 'step' : undefined}
                  className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                    isActive
                      ? 'bg-accent-primary text-white'
                      : isCompleted
                        ? 'bg-accent-primary/10 text-accent-primary cursor-pointer hover:bg-accent-primary/20'
                        : 'bg-surface-hover text-text-tertiary cursor-default'
                  }`}
                >
                  <span className="hidden sm:inline">{stepLabels[step]}</span>
                  <span className="sm:hidden">{idx + 1}</span>
                </button>
              </li>
            );
          })}
        </ol>
      </nav>

      {/* Step 1: University target selector */}
      {currentStep === 'university' && (
        <div className="mt-6" role="group" aria-labelledby="step-university-label">
          <p id="step-university-label" className="text-sm font-semibold text-text-primary">
            {isId ? 'Universitas mana yang kamu tuju?' : 'Which universities are you targeting?'}
          </p>
          <p className="mt-1 text-xs text-text-secondary">
            {isId ? 'Pilih satu atau lebih, atau pilih "Semua".' : 'Select one or more, or choose "Any".'}
          </p>

          <div className="mt-3 flex flex-wrap gap-2" role="listbox" aria-multiselectable="true">
            {/* "Any" chip */}
            <button
              type="button"
              role="option"
              aria-selected={anyUniversity}
              onClick={selectAny}
              className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                anyUniversity
                  ? 'border-accent-primary bg-accent-primary text-white'
                  : 'border-border-subtle bg-surface-root text-text-secondary hover:border-accent-primary hover:text-accent-primary'
              }`}
            >
              {isId ? 'Semua' : 'Any'}
            </button>

            {universityIds.map(uniId => {
              const isSelected = selectedUnis.has(uniId);
              return (
                <button
                  key={uniId}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => toggleUni(uniId)}
                  className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                    isSelected
                      ? 'border-accent-primary bg-accent-primary text-white'
                      : 'border-border-subtle bg-surface-root text-text-secondary hover:border-accent-primary hover:text-accent-primary'
                  }`}
                >
                  {uniId.toUpperCase()}
                </button>
              );
            })}
          </div>

          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={goNext}
              disabled={!canProceedFromUniversity}
              className="rounded-lg bg-accent-primary px-4 py-2 text-sm font-medium text-white transition-opacity disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90"
            >
              {isId ? 'Lanjut' : 'Next'}
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Monthly family income */}
      {currentStep === 'income' && (
        <div className="mt-6" role="group" aria-labelledby="step-income-label">
          <p id="step-income-label" className="text-sm font-semibold text-text-primary">
            {isId ? 'Berapa penghasilan keluarga per bulan?' : 'What is your family\'s monthly income?'}
          </p>
          <p className="mt-1 text-xs text-text-secondary">
            {isId
              ? 'Ini membantu memfilter beasiswa berbasis kebutuhan. Data tidak disimpan.'
              : 'This helps filter need-based scholarships. Data is not stored.'}
          </p>

          <div className="mt-3 grid gap-2 sm:grid-cols-2" role="radiogroup" aria-labelledby="step-income-label">
            {INCOME_OPTIONS.map(opt => {
              const isSelected = selectedIncome === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  onClick={() => setSelectedIncome(opt.id)}
                  className={`rounded-lg border px-4 py-3 text-left text-sm font-medium transition-colors ${
                    isSelected
                      ? 'border-accent-primary bg-accent-primary/10 text-accent-primary'
                      : 'border-border-subtle bg-surface-root text-text-secondary hover:border-accent-primary'
                  }`}
                >
                  {isId ? opt.labelId : opt.labelEn}
                </button>
              );
            })}
          </div>

          <div className="mt-4 flex justify-between">
            <button
              type="button"
              onClick={goBack}
              className="rounded-lg border border-border-subtle px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-hover transition-colors"
            >
              {isId ? 'Kembali' : 'Back'}
            </button>
            <button
              type="button"
              onClick={goNext}
              disabled={!canProceedFromIncome}
              className="rounded-lg bg-accent-primary px-4 py-2 text-sm font-medium text-white transition-opacity disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90"
            >
              {isId ? 'Lanjut' : 'Next'}
            </button>
          </div>
        </div>
      )}

      {/* Step 3: GPA range */}
      {currentStep === 'gpa' && (
        <div className="mt-6" role="group" aria-labelledby="step-gpa-label">
          <p id="step-gpa-label" className="text-sm font-semibold text-text-primary">
            {isId ? 'Berapa IPK kamu saat ini?' : 'What is your current GPA?'}
          </p>
          <p className="mt-1 text-xs text-text-secondary">
            {isId
              ? 'Ini membantu menandai beasiswa prestasi yang relevan.'
              : 'This helps highlight relevant merit-based scholarships.'}
          </p>

          <div className="mt-3 grid gap-2 sm:grid-cols-3" role="radiogroup" aria-labelledby="step-gpa-label">
            {GPA_OPTIONS.map(opt => {
              const isSelected = selectedGpa === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  onClick={() => setSelectedGpa(opt.id)}
                  className={`rounded-lg border px-4 py-3 text-center text-sm font-medium transition-colors ${
                    isSelected
                      ? 'border-accent-primary bg-accent-primary/10 text-accent-primary'
                      : 'border-border-subtle bg-surface-root text-text-secondary hover:border-accent-primary'
                  }`}
                >
                  {isId ? opt.labelId : opt.labelEn}
                </button>
              );
            })}
          </div>

          <div className="mt-4 flex justify-between">
            <button
              type="button"
              onClick={goBack}
              className="rounded-lg border border-border-subtle px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-hover transition-colors"
            >
              {isId ? 'Kembali' : 'Back'}
            </button>
            <button
              type="button"
              onClick={goNext}
              disabled={!canProceedFromGpa}
              className="rounded-lg bg-accent-primary px-4 py-2 text-sm font-medium text-white transition-opacity disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90"
            >
              {isId ? 'Lihat Hasil' : 'View Results'}
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Results */}
      {currentStep === 'results' && (
        <div className="mt-6">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-text-primary">
              {isId
                ? `${filteredScholarships.length} beasiswa ditemukan`
                : `${filteredScholarships.length} scholarship${filteredScholarships.length !== 1 ? 's' : ''} found`}
            </p>
            <button
              type="button"
              onClick={resetAll}
              className="text-xs font-medium text-accent-primary hover:underline"
            >
              {isId ? 'Ulangi penyaringan' : 'Start over'}
            </button>
          </div>

          {/* Summary of selections */}
          <div className="mt-2 flex flex-wrap gap-1.5">
            <span className="inline-flex items-center rounded-full bg-surface-hover px-2.5 py-0.5 text-xs text-text-secondary">
              {anyUniversity
                ? (isId ? 'Semua universitas' : 'Any university')
                : `${selectedUnis.size} ${isId ? 'universitas' : (selectedUnis.size === 1 ? 'university' : 'universities')}`}
            </span>
            <span className="inline-flex items-center rounded-full bg-surface-hover px-2.5 py-0.5 text-xs text-text-secondary">
              {INCOME_OPTIONS.find(o => o.id === selectedIncome)?.[isId ? 'labelId' : 'labelEn'] ?? ''}
            </span>
            <span className="inline-flex items-center rounded-full bg-surface-hover px-2.5 py-0.5 text-xs text-text-secondary">
              {GPA_OPTIONS.find(o => o.id === selectedGpa)?.[isId ? 'labelId' : 'labelEn'] ?? ''}
            </span>
          </div>

          {/* Scholarship cards */}
          {filteredScholarships.length === 0 ? (
            <div className="mt-6 rounded-lg border border-border-subtle bg-surface-root p-6 text-center">
              <p className="text-sm text-text-secondary">
                {isId
                  ? 'Tidak ada beasiswa yang cocok dengan kriteria kamu. Coba ubah pilihan universitas.'
                  : 'No scholarships match your criteria. Try changing your university selection.'}
              </p>
              <button
                type="button"
                onClick={resetAll}
                className="mt-3 rounded-lg bg-accent-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90 transition-opacity"
              >
                {isId ? 'Coba lagi' : 'Try again'}
              </button>
            </div>
          ) : (
            <div className="mt-4 space-y-3">
              {filteredScholarships.map(s => {
                const badge = getEligibilityBadge(s.scholarshipType, selectedIncome, selectedGpa, isId);
                const isExpanded = expandedCard === s.id;
                const name = isId ? s.nameId : s.nameEn;
                const description = isId ? s.descriptionId : s.descriptionEn;
                const eligibility = isId ? s.eligibilityId : s.eligibilityEn;
                const benefits = isId ? s.benefitsId : s.benefitsEn;

                return (
                  <article
                    key={s.id}
                    className="rounded-xl border border-border-subtle bg-surface-root p-4 transition-shadow hover:shadow-md"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h4 className="text-base font-bold text-text-primary truncate">
                          {name}
                        </h4>
                        <p className="mt-0.5 text-xs text-text-tertiary">
                          {s.provider}
                        </p>
                      </div>
                      <span
                        className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          badge.likely
                            ? 'badge-success'
                            : 'badge-warning'
                        }`}
                      >
                        {badge.label}
                      </span>
                    </div>

                    {/* Quick info row */}
                    <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                      {s.amount && (
                        <div className="flex items-center gap-1">
                          <span className="text-text-secondary">{isId ? 'Nilai:' : 'Amount:'}</span>
                          <span className="font-medium text-text-primary">{s.amount}</span>
                        </div>
                      )}
                      {s.deadline && (
                        <div className="flex items-center gap-1">
                          <span className="text-text-secondary">{isId ? 'Tenggat:' : 'Deadline:'}</span>
                          <span className="font-medium text-text-primary">{formatDateShortYear(s.deadline, lang)}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <span className="text-text-secondary">{isId ? 'Tipe:' : 'Type:'}</span>
                        <span className="font-medium text-text-primary">{s.scholarshipType}</span>
                      </div>
                    </div>

                    {/* Expand/collapse button */}
                    <button
                      type="button"
                      onClick={() => setExpandedCard(isExpanded ? null : s.id)}
                      aria-expanded={isExpanded}
                      className="mt-3 w-full rounded-lg border border-border-subtle px-3 py-1.5 text-xs font-medium text-accent-primary hover:bg-surface-hover transition-colors"
                    >
                      {isExpanded
                        ? (isId ? 'Sembunyikan detail' : 'Hide details')
                        : (isId ? 'Lihat detail' : 'View details')}
                    </button>

                    {/* Expanded details */}
                    {isExpanded && (
                      <div className="mt-3 space-y-3 border-t border-border-subtle pt-3">
                        {/* Description */}
                        <div>
                          <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                            {isId ? 'Deskripsi' : 'Description'}
                          </p>
                          <p className="mt-1 text-sm text-text-primary">{description}</p>
                        </div>

                        {/* Eligibility */}
                        {eligibility.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                              {isId ? 'Persyaratan' : 'Eligibility'}
                            </p>
                            <ul className="mt-1 space-y-1">
                              {eligibility.map((item, i) => (
                                <li key={i} className="flex items-start gap-1.5 text-sm text-text-primary">
                                  <span className="shrink-0 text-text-tertiary" aria-hidden="true">-</span>
                                  {item}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Benefits */}
                        {benefits.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-semantic-success uppercase tracking-wider">
                              {isId ? 'Manfaat' : 'Benefits'}
                            </p>
                            <ul className="mt-1 space-y-1">
                              {benefits.map((item, i) => (
                                <li key={i} className="flex items-start gap-1.5 text-sm text-text-primary">
                                  <span className="shrink-0 text-green-500" aria-hidden="true">+</span>
                                  {item}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Apply link */}
                        {(s.url || s.linkSlug) && (
                          <div className="flex gap-3">
                            {s.url && (
                              <a
                                href={s.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 rounded-lg bg-accent-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90 transition-opacity"
                              >
                                {isId ? 'Daftar' : 'Apply'}
                                <span aria-hidden="true">&rarr;</span>
                              </a>
                            )}
                            {s.linkSlug && (
                              <a
                                href={`/${s.linkSlug}`}
                                className="inline-flex items-center gap-1 text-sm font-medium text-accent-primary hover:underline"
                              >
                                {isId ? 'Info lengkap' : 'Full details'}
                                <span aria-hidden="true">&rarr;</span>
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          )}

          {/* Back to edit */}
          <div className="mt-4">
            <button
              type="button"
              onClick={goBack}
              className="rounded-lg border border-border-subtle px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-hover transition-colors"
            >
              {isId ? 'Kembali ke IPK' : 'Back to GPA'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
